/**
 * @fileoverview Visão de Calendário do CRM. Renderiza, em grade de Mês ou Semana,
 * todos os cards (leads/tasks) que possuem Prazo (SLA) preenchido, no dia do
 * vencimento. Destaca hoje, a semana atual e os feriados nacionais do Brasil.
 * Estética alinhada à página de Boards (dark-mode, acento índigo, ícones lucide).
 */

import React, { useState, useMemo, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import moment from "moment";
import "moment/locale/pt-br";

import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  Flag,
} from "lucide-react";

import { computeSlaStatus } from "../LeadShared";
import { getHoliday, dateKey } from "../../utils/brazilianHolidays";

const ptMoment = (d) => moment(d).locale("pt-br");

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const WEEKDAYS_MINI = ["D", "S", "T", "Q", "Q", "S", "S"];
const HOLIDAY = "#e11d48";

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfWeek = (d) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay());
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const sameDay = (a, b) => dateKey(a) === dateKey(b);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const surface = dark ? "rgba(14,16,22,0.85)" : "#FFFFFF";
  const border = dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)";
  const gridLine = dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.07)";
  const text = dark ? "#E8EAED" : "#0F172A";
  const muted = dark ? "#9CA3AF" : "#64748B";
  const faint = dark ? "#6B7280" : "#94A3B8";
  const accent = dark ? "#A5B4FC" : "#3D5CFD";

  return {
    root: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },

    /* ── Toolbar ── */
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: { gap: 6 },
    },
    navGroup: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      [theme.breakpoints.down("xs")]: { gap: 6, flex: "1 1 auto", minWidth: 0 },
    },
    navBtns: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      padding: 3,
      borderRadius: 11,
      background: surface,
      border,
    },
    navBtn: {
      width: 30,
      height: 30,
      borderRadius: 8,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: muted,
      transition: "background .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
        color: text,
      },
    },
    monthTitle: {
      fontSize: "1.18rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: text,
      minWidth: 188,
      lineHeight: 1.1,
      border: "none",
      background: "transparent",
      padding: 0,
      fontFamily: "inherit",
      cursor: "pointer",
      textAlign: "left",
      transition: "color .15s",
      "&:hover": { color: accent },
      "& small": { color: faint, fontWeight: 600, marginLeft: 6, fontSize: "0.78em" },
      [theme.breakpoints.down("xs")]: {
        fontSize: "1rem",
        minWidth: 0,
        flex: "1 1 auto",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      },
    },

    /* ── Segmented Mês/Semana ── */
    segmented: {
      display: "inline-flex",
      padding: 3,
      borderRadius: 11,
      background: surface,
      border,
      gap: 2,
      [theme.breakpoints.down("xs")]: { marginLeft: "auto", flexShrink: 0 },
    },
    segBtn: {
      height: 30,
      padding: "0 12px",
      borderRadius: 8,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      fontSize: 12.5,
      fontWeight: 500,
      fontFamily: "inherit",
      color: muted,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      transition: "background .15s, color .15s",
      "&:hover": { color: text },
      [theme.breakpoints.down("xs")]: { padding: "0 10px", gap: 5 },
    },
    segBtnActive: {
      background: "rgba(71,71,71,0.22)",
      color: "#ffffff",
      "&:hover": {
        background: "rgba(134,134,134,0.20)",
        color: "#ffffff",
      },
    },

    /* ── Grade ── */
    calendar: {
      position: "relative",
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      borderRadius: 16,
      overflow: "hidden",
      background: surface,
      border,
      boxShadow: dark
        ? "0 1px 0 rgba(255,255,255,0.02) inset"
        : "0 1px 2px rgba(15,23,42,0.04)",
    },
    weekHead: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      flexShrink: 0,
      borderBottom: `1px solid ${gridLine}`,
    },
    weekHeadCell: {
      padding: "10px 12px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: faint,
      textAlign: "left",
      [theme.breakpoints.down("xs")]: {
        padding: "8px 0",
        textAlign: "center",
        letterSpacing: 0,
      },
    },
    weekHeadWeekend: { color: dark ? "#7c7f88" : "#b4bdca" },

    grid: {
      flex: 1,
      minHeight: 0,
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      animation: "$calFade .26s ease",
    },
    gridMonth: { gridAutoRows: "1fr" },
    gridWeek: { gridTemplateRows: "1fr" },

    "@keyframes calFade": {
      from: { opacity: 0, transform: "translateY(4px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },

    cell: {
      position: "relative",
      minWidth: 0,
      borderRight: `1px solid ${gridLine}`,
      borderBottom: `1px solid ${gridLine}`,
      padding: "6px 7px 8px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
      overflow: "hidden",
      transition: "background .15s",
      "&:nth-child(7n)": { borderRight: "none" },
      [theme.breakpoints.down("xs")]: { padding: "4px 3px 6px", gap: 3 },
    },
    cellWeek: { padding: "8px 9px 10px", gap: 5 },
    cellOutside: {
      background: dark ? "rgba(255,255,255,0.012)" : "rgba(15,23,42,0.018)",
    },
    cellCurrentWeek: {
      background: dark ? "rgba(99,102,241,0.05)" : "rgba(61,92,253,0.035)",
    },
    cellHoliday: {
      background: dark ? "rgba(225,29,72,0.07)" : "rgba(225,29,72,0.05)",
    },
    cellToday: {
      boxShadow: `inset 0 2px 0 ${accent}`,
    },

    cellHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 4,
      minHeight: 22,
    },
    dayNum: {
      fontSize: 12.5,
      fontWeight: 600,
      color: muted,
      lineHeight: 1,
      width: 22,
      height: 22,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
    },
    dayNumOutside: { color: faint, opacity: 0.55 },
    dayNumToday: {
      background: accent,
      color: "#FFFFFF",
      fontWeight: 700,
    },
    dayNumHoliday: { color: HOLIDAY, fontWeight: 700 },

    holidayTag: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      maxWidth: "100%",
      padding: "2px 6px",
      borderRadius: 6,
      background: dark ? "rgba(225,29,72,0.14)" : "rgba(225,29,72,0.10)",
      color: HOLIDAY,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.01em",
      lineHeight: 1.25,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    holidayTagText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },

    events: {
      display: "flex",
      flexDirection: "column",
      gap: 3,
      minHeight: 0,
      overflow: "hidden",
    },
    eventsWeek: { overflowY: "auto", gap: 4 },

    chip: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      width: "100%",
      padding: "4px 7px",
      borderRadius: 7,
      border: "none",
      cursor: "pointer",
      textAlign: "left",
      fontFamily: "inherit",
      position: "relative",
      overflow: "hidden",
      transition: "transform .12s, filter .15s",
      "&:hover": { filter: "brightness(1.06)", transform: "translateX(1px)" },
      "&:before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        background: "currentColor",
      },
      [theme.breakpoints.down("xs")]: { padding: "3px 5px 3px 8px", gap: 4 },
    },
    chipDot: {
      width: 7,
      height: 7,
      borderRadius: "50%",
      flexShrink: 0,
      boxShadow: "0 0 0 2px rgba(0,0,0,0.04)",
    },
    chipName: {
      flex: 1,
      minWidth: 0,
      fontSize: 11.5,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: { fontSize: 10.5 },
    },
    chipTime: {
      fontSize: 10,
      fontWeight: 700,
      opacity: 0.85,
      flexShrink: 0,
      fontVariantNumeric: "tabular-nums",
    },

    moreBtn: {
      marginTop: 1,
      padding: "2px 6px",
      borderRadius: 6,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 10.5,
      fontWeight: 700,
      color: muted,
      textAlign: "left",
      transition: "background .12s, color .12s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
        color: text,
      },
    },

    /* ── Popover "+N mais" ── */
    dayPopover: {
      "& .MuiPopover-paper": {
        borderRadius: 12,
        background: dark ? "rgba(14,16,22,0.98)" : "#FFFFFF",
        border,
        boxShadow: dark
          ? "0 20px 60px -12px rgba(0,0,0,0.7)"
          : "0 20px 60px -12px rgba(15,23,42,0.18)",
        width: 268,
        maxHeight: 360,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        marginTop: 4,
      },
    },
    dayPopoverHead: {
      padding: "11px 14px",
      borderBottom: `1px solid ${gridLine}`,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    dayPopoverDate: { fontSize: 13, fontWeight: 700, color: text, lineHeight: 1.2 },
    dayPopoverSub: { fontSize: 11, color: faint, marginTop: 1 },
    dayPopoverList: { padding: 7, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 },

    /* ── Estados ── */
    overlay: {
      position: "absolute",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      background: dark ? "rgba(10,10,10,0.45)" : "rgba(248,250,252,0.55)",
      backdropFilter: "blur(1px)",
      zIndex: 2,
      color: muted,
    },
    emptyHint: {
      position: "absolute",
      left: "50%",
      bottom: 18,
      transform: "translateX(-50%)",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      borderRadius: 999,
      background: dark ? "rgba(14,16,22,0.92)" : "#FFFFFF",
      border,
      color: muted,
      fontSize: 12.5,
      fontWeight: 500,
      boxShadow: dark
        ? "0 10px 30px -10px rgba(0,0,0,0.6)"
        : "0 10px 30px -10px rgba(15,23,42,0.16)",
      zIndex: 1,
    },
  };
});

/* Chip de um card no calendário, colorido pelo tom do SLA.
   compact = layout enxuto (mobile): só o nome, sem ponto/horário. */
const EventChip = ({ lead, classes, dark, compact = false, onClick }) => {
  const isDone = !!lead.isCompleted;
  const sla = computeSlaStatus(lead.slaDate);
  const tone = isDone
    ? (dark ? "#6B7280" : "#9AA4B2")
    : (sla?.color || "#64748B");
  const bg = isDone
    ? (dark ? "rgba(148,163,184,0.07)" : "rgba(100,116,139,0.07)")
    : (sla?.bg || (dark ? "rgba(148,163,184,0.12)" : "rgba(100,116,139,0.10)"));
  const time = ptMoment(lead.slaDate).format("HH:mm");

  return (
    <Tooltip
      title={`${lead.name || "Sem nome"} · ${time}${lead.boardName ? ` · ${lead.boardName}` : ""}${
        lead.statusName ? ` · ${lead.statusName}` : ""
      }${isDone ? " · ✓ concluído" : ""}`}
      placement="top"
      enterDelay={350}
    >
      <button
        type="button"
        className={classes.chip}
        style={{ background: bg, color: tone, opacity: isDone ? 0.6 : 1 }}
        onClick={(e) => {
          e.stopPropagation();
          onClick(lead);
        }}
      >
        {!compact && (
          <span
            className={classes.chipDot}
            style={{ background: isDone ? tone : (lead.boardColor || tone) }}
          />
        )}
        <span
          className={classes.chipName}
          style={{
            color: isDone ? tone : (dark ? "#E8EAED" : "#0F172A"),
            textDecoration: isDone ? "line-through" : "none",
          }}
        >
          {lead.name || "Sem nome"}
        </span>
        {!compact && <span className={classes.chipTime}>{time}</span>}
      </button>
    </Tooltip>
  );
};

const CrmCalendarView = ({
  events = [],
  loading = false,
  searchParam = "",
  assignedUserFilter = "",
  onSelectLead,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [dayPopover, setDayPopover] = useState(null); // { anchorEl, date, items }

  const today = useMemo(() => startOfDay(new Date()), []);

  /* Filtra eventos por busca/responsável e indexa por dia. */
  const eventsByDay = useMemo(() => {
    const q = (searchParam || "").trim().toLowerCase();
    const map = new Map();
    events.forEach((ev) => {
      if (!ev.slaDate) return;
      if (q && !(ev.name || "").toLowerCase().includes(q)) return;
      if (assignedUserFilter === "unassigned") {
        if (ev.assignedUserId) return;
      } else if (assignedUserFilter) {
        if (String(ev.assignedUserId) !== String(assignedUserFilter)) return;
      }
      const d = new Date(ev.slaDate);
      if (Number.isNaN(d.getTime())) return;
      const k = dateKey(d);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    });
    map.forEach((arr) =>
      arr.sort((a, b) => new Date(a.slaDate) - new Date(b.slaDate))
    );
    return map;
  }, [events, searchParam, assignedUserFilter]);

  const totalVisible = useMemo(() => {
    let n = 0;
    eventsByDay.forEach((arr) => (n += arr.length));
    return n;
  }, [eventsByDay]);

  /* Dias exibidos conforme a visão. */
  const days = useMemo(() => {
    if (view === "week") {
      const start = startOfWeek(cursor);
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(monthStart);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [cursor, view]);

  const currentWeekStartKey = useMemo(() => dateKey(startOfWeek(today)), [today]);

  const navigate = useCallback(
    (action) => {
      if (action === "today") {
        setCursor(startOfDay(new Date()));
        return;
      }
      const delta = action === "next" ? 1 : -1;
      setCursor((prev) => {
        const next = new Date(prev);
        if (view === "week") next.setDate(next.getDate() + 7 * delta);
        else next.setMonth(next.getMonth() + delta);
        return next;
      });
    },
    [view]
  );

  const title = useMemo(() => {
    if (view === "week") {
      const start = startOfWeek(cursor);
      const end = addDays(start, 6);
      const sameMonth = start.getMonth() === end.getMonth();
      const startStr = ptMoment(start).format(sameMonth ? "D" : "D MMM");
      const endStr = ptMoment(end).format("D MMM");
      return { main: `${startStr} – ${endStr}`, year: String(end.getFullYear()) };
    }
    return {
      main: cap(ptMoment(cursor).format("MMMM")),
      year: String(cursor.getFullYear()),
    };
  }, [cursor, view]);

  const maxVisible = view === "week" ? 99 : 3;

  const openDayPopover = (e, date, items) => {
    e.stopPropagation();
    setDayPopover({ anchorEl: e.currentTarget, date, items });
  };

  const renderCell = (day) => {
    const k = dateKey(day);
    const inMonth = view === "week" || day.getMonth() === cursor.getMonth();
    const isToday = sameDay(day, today);
    const holiday = getHoliday(day);
    const inCurrentWeek = dateKey(startOfWeek(day)) === currentWeekStartKey;
    const items = eventsByDay.get(k) || [];
    const visible = items.slice(0, maxVisible);
    const overflow = items.length - visible.length;

    return (
      <div
        key={k}
        className={[
          classes.cell,
          view === "week" ? classes.cellWeek : "",
          !inMonth ? classes.cellOutside : "",
          holiday ? classes.cellHoliday : inCurrentWeek && view === "month" ? classes.cellCurrentWeek : "",
          isToday ? classes.cellToday : "",
        ].join(" ")}
      >
        <div className={classes.cellHead}>
          <span
            className={[
              classes.dayNum,
              !inMonth ? classes.dayNumOutside : "",
              isToday ? classes.dayNumToday : "",
              holiday && !isToday ? classes.dayNumHoliday : "",
            ].join(" ")}
          >
            {day.getDate()}
          </span>
          {holiday && (
            <Tooltip title={holiday.name} placement="top" enterDelay={300}>
              <span className={classes.holidayTag}>
                <Flag size={10} strokeWidth={2.25} style={{ flexShrink: 0 }} />
                <span className={classes.holidayTagText}>{holiday.name}</span>
              </span>
            </Tooltip>
          )}
        </div>

        <div className={[classes.events, view === "week" ? classes.eventsWeek : ""].join(" ")}>
          {visible.map((lead) => (
            <EventChip
              key={lead.id}
              lead={lead}
              classes={classes}
              dark={isDark}
              compact={isMobile}
              onClick={onSelectLead}
            />
          ))}
          {overflow > 0 && (
            <button
              type="button"
              className={classes.moreBtn}
              onClick={(e) => openDayPopover(e, day, items)}
            >
              +{overflow} mais
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.root}>
      {/* Toolbar */}
      <div className={classes.toolbar}>
        <div className={classes.navGroup}>
          <div className={classes.navBtns}>
            <button type="button" className={classes.navBtn} onClick={() => navigate("prev")} aria-label="Anterior">
              <ChevronLeft size={18} strokeWidth={2} />
            </button>
            <button type="button" className={classes.navBtn} onClick={() => navigate("next")} aria-label="Próximo">
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </div>
          <Tooltip title="Ir para hoje" placement="bottom" enterDelay={400}>
            <button type="button" className={classes.monthTitle} onClick={() => navigate("today")}>
              {title.main} <small>{title.year}</small>
            </button>
          </Tooltip>
        </div>

        <div className={classes.segmented}>
          <button
            type="button"
            className={`${classes.segBtn} ${view === "month" ? classes.segBtnActive : ""}`}
            onClick={() => setView("month")}
          >
            <CalendarDays size={14} strokeWidth={2} />
            Mês
          </button>
          <button
            type="button"
            className={`${classes.segBtn} ${view === "week" ? classes.segBtnActive : ""}`}
            onClick={() => setView("week")}
          >
            <CalendarRange size={14} strokeWidth={2} />
            Semana
          </button>
        </div>
      </div>

      {/* Calendário */}
      <div className={classes.calendar}>
        <div className={classes.weekHead}>
          {(isMobile ? WEEKDAYS_MINI : WEEKDAYS).map((wd, i) => (
            <div
              key={i}
              className={`${classes.weekHeadCell} ${i === 0 || i === 6 ? classes.weekHeadWeekend : ""}`}
            >
              {wd}
            </div>
          ))}
        </div>

        <div
          key={`${view}-${dateKey(days[0])}`}
          className={`${classes.grid} ${view === "week" ? classes.gridWeek : classes.gridMonth}`}
        >
          {days.map(renderCell)}
        </div>

        {!loading && totalVisible === 0 && (
          <div className={classes.emptyHint}>
            <CalendarDays size={15} strokeWidth={1.9} />
            Nenhum card com prazo neste período
          </div>
        )}

        {loading && (
          <div className={classes.overlay}>
            <CircularProgress size={28} />
            <span style={{ fontSize: 13 }}>Carregando prazos…</span>
          </div>
        )}
      </div>

      {/* Popover de eventos do dia */}
      <Popover
        open={Boolean(dayPopover)}
        anchorEl={dayPopover?.anchorEl}
        onClose={() => setDayPopover(null)}
        className={classes.dayPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {dayPopover && (
          <>
            <div className={classes.dayPopoverHead}>
              <CalendarDays size={16} strokeWidth={1.9} style={{ opacity: 0.7 }} />
              <div>
                <div className={classes.dayPopoverDate}>
                  {cap(ptMoment(dayPopover.date).format("dddd, D [de] MMMM"))}
                </div>
                <div className={classes.dayPopoverSub}>
                  {dayPopover.items.length} {dayPopover.items.length === 1 ? "card" : "cards"} com prazo
                </div>
              </div>
            </div>
            <div className={classes.dayPopoverList}>
              {dayPopover.items.map((lead) => (
                <EventChip
                  key={lead.id}
                  lead={lead}
                  classes={classes}
                  dark={isDark}
                  onClick={(l) => {
                    setDayPopover(null);
                    onSelectLead(l);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </Popover>
    </div>
  );
};

export default CrmCalendarView;
